<template>
    <div class="app-body"
         id="view">
        <div class="padding">
            <!-- ////////////////// Filters section //////////////////  -->

            <div class="row mb-3 d-flex align-items-center justify-content-between">
                <div class="px-3">
                    <div v-if="records.length">
                        <span class="_700">Results:</span> {{ pagination.total }}
                    </div>
                </div>

                <div class="w-80 d-flex justify-content-end px-3 pt-3">

                    <div class="mr-2 w-50">
                        <el-input
                            placeholder="Search for an email, company name, referrer ..."
                            prefix-icon="el-icon-search"
                            v-model="filters.search_text"
                            @clear="records = []"
                            @keyup.enter.native="handleSearch"
                            clearable>
                        </el-input>
                    </div>

                    <div class="mr-2"
                         v-if="!auth?.user?.profile?.is_reseller">
                        <el-select
                            v-model="filters.pre_signup_types"
                            multiple
                            collapse-tags
                            collapse-tags-tooltip
                            @clear="filters.pre_signup_types = null"
                            placeholder="Select a source"
                            style="width: 240px"
                        >
                            <el-option
                                v-for="item in pre_signup_type_options"
                                :key="item.value"
                                :label="item.label"
                                :value="item.value">
                            </el-option>
                        </el-select>
                    </div>

                    <div>
                        <el-button
                            v-if="showClearFiltersButton"
                            @click="clearFilters()">
                            Reset Filters
                        </el-button>

                        <el-button
                            type="success"
                            @click="handleSearch"
                            :disabled="searching">
                            Search
                        </el-button>
                    </div>
                </div>
            </div>

            <!-- ////////////////// Table section //////////////////  -->

            <div class="card p-3"
                 v-loading="searching"
                 id="container-table-presignups"
            >
                <div
                    v-if="records.length"
                    class="p-3">
                    <el-table
                        stripe
                        :data="records"
                        style="width: 100%">
                        <el-table-column
                            sortable
                            prop="business_name"
                            label="Company Name"
                        >
                            <template v-slot="scope">
                                <p class="mb-0">{{ scope.row.business_name }}</p>
                            </template>
                        </el-table-column>
                        <el-table-column label="Source"
                                         v-if="!auth?.user?.profile?.is_reseller">
                            <template v-slot="scope">
                            <span>
                                {{ submissionTypeName(scope.row.submission_type) }}
                            </span>
                            </template>
                        </el-table-column>
                        <el-table-column
                            sortable
                            prop="first_name"
                            label="Admin Name"
                        >
                            <template v-slot="scope">
                                <span>{{ `${scope.row.first_name} ${scope.row.last_name}` }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="Phone Number">
                            <template v-slot="scope">
                                <span>{{ scope.row.phone_number }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="Email">
                            <template v-slot="scope">
                                <span>{{ scope.row.email }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="Referrer"
                                         v-if="!auth?.user?.profile?.is_reseller">
                            <template v-slot="scope">
                                <span>{{ scope.row.referrer }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="Status">
                            <template v-slot="scope">
                                <span>{{ scope.row.status ?? '-' }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column
                            sortable
                            prop="created_at"
                            label="Date created"
                        >
                            <template v-slot="scope">
                                <span>{{ scope.row.created_at }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column
                            sortable
                            prop="updated_at"
                            label="Date updated (used for token expiration)">
                            <template v-slot="scope">
                                <span>{{ scope.row.updated_at }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column
                            label="Actions"
                            width="200">
                            <template v-slot="scope">
                                <button
                                    class="btn btn-sm btn-block btn-info"
                                    size="mini"
                                    icon="el-icon-info"
                                    @click="showCompanyInfoDialog(scope.row)">Company Info
                                </button>
                                <button
                                    v-if="canManagePreSignupEmail(scope.row.status)"
                                    class="btn btn-sm btn-block btn-success"
                                    size="mini"
                                    icon="el-icon-message"
                                    @click="showEmailActionsDialog(scope.row, false)">Resend Email
                                </button>
                                <button
                                    v-if="canManagePreSignupEmail(scope.row.status)"
                                    class="btn btn-sm btn-block btn-primary"
                                    size="mini"
                                    icon="el-icon-edit"
                                    @click="showEmailActionsDialog(scope.row, true)">Update Email
                                </button>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>

                <div v-else
                     class="message-centered">
                    <div v-if="searching"
                         class="w-100">
                        <h3 class="text-center">Loading...</h3>
                    </div>

                    <div v-else-if="no_results"
                         class="w-100">
                        <h3 class="text-center">No results.</h3>
                    </div>

                    <div v-else
                         class="w-100 search-resource-text">
                        <div class="text-center">
                            <i class="fa fa-search"></i>
                            <p class="mb-0">Pre-sign up records in pending status </p>
                        </div>
                    </div>
                </div>

                <!-- ///////////////////// Paginator ///////////////////// -->

                <div class="mt-3 mb-3"
                     v-if="records.length > 0 && pagination.total > pagination.per_page">
                    <el-pagination :page-size="pagination.per_page"
                                   :page-sizes="[10, 15, 20, 25]"
                                   :total="pagination.total"
                                   background
                                   class="pull-right"
                                   layout="sizes, prev, pager, next"
                                   @current-change="paginationChangePage"
                                   @size-change="paginationChangeSize">
                    </el-pagination>
                </div>
            </div>

            <!-- ////////////////// Resend Email Dialog //////////////////  -->

            <el-dialog
                :title="email_actions.dialog_title"
                :visible.sync="email_actions.display_dialog && email_actions.pre_signup !== null"
                @close="hideEmailActionsDialog"
                width="30%">
                <div class="dialog-text">
                    <el-form ref="resend_email"
                             v-loading="email_actions.action_in_progress"
                             :model="email_actions"
                             :rules="email_actions.rules"
                    >
                        <p class="form-head-message mb-2" v-if="!email_actions.updating_email">
                            We will resend the verification email for <span class="text-dark-greenish">{{ email_actions.pre_signup?.business_name }}</span> to the current address <span class="text-dark-greenish">{{ email_actions.pre_signup?.email }}</span>.
                        </p>
                        <template v-if="email_actions.updating_email">
                            <p class="form-head-message mb-2">
                                This will update the email address in the Pre-signup record for <span class="text-dark-greenish">{{ email_actions.pre_signup?.business_name }}</span> and send the verification email to the new address
                            </p>
                            <el-form-item label="New email address"
                                          prop="new_email">
                                <el-input placeholder="joe@example.com"
                                          v-model="email_actions.new_email"
                                          @input="preValidateForm('resend_email')">
                                </el-input>
                            </el-form-item>
                        </template>
                    </el-form>
                </div>
                <span slot="footer" class="dialog-footer">
                    <el-button @click="hideEmailActionsDialog">Cancel</el-button>
                    <el-button type="success"
                               @click="handleEmailActions()"
                               :disabled="!isEmailActionFormValid() || email_actions.action_in_progress">
                        {{ email_actions.updating_email ? 'Update email and resend' : 'Resend'}}
                    </el-button>
                </span>
            </el-dialog>

            <!-- Company Info Dialog -->
            <el-dialog
                title="Company Information"
                :visible.sync="companyInfoDialog.visible"
                width="50%">
                <div v-if="companyInfoDialog.preSignup">
                    <div class="row">
                        <div class="col-md-6">
                            <h5>Company Details</h5>
                            <p><strong>Company Size:</strong> {{ companyInfoDialog.preSignup.company_size || 'Not specified' }}</p>
                            <p><strong>Number of Licenses:</strong> {{ companyInfoDialog.preSignup.team_size || 'Not specified' }}</p>
                        </div>
                        <div class="col-md-6">
                            <h5>Technology & Teams</h5>
                            <p><strong>Company CRM:</strong></p>
                            <ul v-if="companyInfoDialog.preSignup.company_crm && companyInfoDialog.preSignup.company_crm.length">
                                <li v-for="crm in companyInfoDialog.preSignup.company_crm" :key="crm">{{ COMPANY_CRM_DISPLAY_NAMES[crm] || crm.charAt(0).toUpperCase() + crm.slice(1) }}</li>
                            </ul>
                            <p v-else>Not specified</p>
                            
                            <p><strong>Teams:</strong></p>
                            <ul v-if="companyInfoDialog.preSignup.teams && companyInfoDialog.preSignup.teams.length">
                                <li v-for="team in companyInfoDialog.preSignup.teams" :key="team">{{ TEAMS_DISPLAY_NAMES[team] || team.charAt(0).toUpperCase() + team.slice(1) }}</li>
                            </ul>
                            <p v-else>Not specified</p>
                        </div>
                    </div>
                </div>
                <span slot="footer" class="dialog-footer">
                    <el-button @click="companyInfoDialog.visible = false">Close</el-button>
                </span>
            </el-dialog>
        </div>
    </div>
</template>

<script>

import {
    PRE_SIGNUP_SUBMISSION_TYPES,
    PRE_SIGNUP_STATUS_APPROVED,
    PRE_SIGNUP_STATUS_INVITED,
    PRE_SIGNUP_STATUS_EXPIRED
} from '../constants/pre-signup';
import {form_validation_mixin, validator_mixin} from '../mixins'
import auth from '../auth';
import { COMPANY_CRM_DISPLAY_NAMES, TEAMS_DISPLAY_NAMES } from '../../user/constants/signup-options'

export default {
    data() {
        return {
            records: [],
            auth: auth,
            COMPANY_CRM_DISPLAY_NAMES,
            TEAMS_DISPLAY_NAMES,
            pagination: {
                per_page: 10,
                current_page: 1,
                total: 0,
            },
            filters: {
                last_search_text: '',
                search_text: '',
                pre_signup_types: null,
            },
            pre_signup_type_options: Object.values(PRE_SIGNUP_SUBMISSION_TYPES).map((type) => ({
                label: type.visible_name,
                value: type.id
            })),
            no_results: false,
            empty_filters: true,
            searching: false,
            email_actions: {
                dialog_title: 'Resend verification email',
                pre_signup: null,
                display_dialog: false,
                updating_email: false,
                action_in_progress: false,
                new_email: null,
                rules: {
                    new_email: [
                        {
                            required: true,
                            message: 'Please provide email address',
                            trigger: 'blur'
                        },
                        {
                            type: 'email',
                            message: 'Please provide a valid email address',
                            trigger: 'blur'
                        }
                    ],
                },
            },
            companyInfoDialog: {
                visible: false,
                preSignup: null
            },
        }
    },

    mixins: [
        form_validation_mixin,
        validator_mixin
    ],

    computed: {
        showClearFiltersButton() {
            this.empty_filters = this.filters.search_text || this.filters.pre_signup_types == null
            return this.empty_filters && !this.auth?.user?.profile?.is_reseller
        },
    },

    methods: {
        canManagePreSignupEmail(status) {
            return [
                PRE_SIGNUP_STATUS_APPROVED,
                PRE_SIGNUP_STATUS_INVITED,
                PRE_SIGNUP_STATUS_EXPIRED
            ].includes(status);
        },

        submissionTypeName(type) {
            const submission_type = Object.values(PRE_SIGNUP_SUBMISSION_TYPES).find((t) => t.id === type)
            return submission_type?.visible_name || PRE_SIGNUP_SUBMISSION_TYPES.admin.visible_name
        },

        clearFilters() {
            this.filters.search_text = ''
            this.filters.pre_signup_types = null
            this.empty_filters = true
        },

        paginationChangePage(val) {
            this.pagination.current_page = val
            this.handleSearch()
        },

        paginationChangeSize(val) {
            this.pagination.current_page = 1
            this.pagination.per_page = val
            this.handleSearch()
        },

        showEmailActionsDialog(pre_signup, updating_email = false) {
            this.email_actions.dialog_title = updating_email ? 'Update email address' : 'Resend verification email'
            this.email_actions.updating_email = updating_email
            this.email_actions.display_dialog = true
            this.email_actions.pre_signup = pre_signup
        },

        hideEmailActionsDialog() {
            this.email_actions.display_dialog = false
            this.resetEmailActionForm()
        },

        isEmailActionFormValid() {
            if (this.email_actions.updating_email) {
                // If the email is to be updated, we will disable the submit button if the new email is not set yet
                if(!this.email_actions.new_email) return false
                return this.validated
            }
            return true;
        },

        resetEmailActionForm() {
            this.email_actions.dialog_title = ''
            this.email_actions.new_email = null
            this.email_actions.pre_signup = null
            this.email_actions.updating_email = false
            this.email_actions.action_in_progress = false
        },

        resetPagination() {
            this.pagination.current_page = 1
            this.pagination.per_page = 10
            this.pagination.total = 0
        },

        handleEmailActions() {
            this.email_actions.action_in_progress = true
            let payload = null
            if(this.email_actions.updating_email){
                payload = {
                    email: this.email_actions.new_email
                }
            }
            axios.post(`/api/admin/pre-signup-records/${this.email_actions.pre_signup.id}/email`, payload)
                .then(res => {
                    this.hideEmailActionsDialog()
                    this.handleSearch()
                    this.$notify({
                        offset: 75,
                        title: 'Verification email',
                        message: 'The verification email was succesfully resent.',
                        type: 'success'
                    })
                }).catch(error => {
                    this.$root.handleErrors(error.response)
                    this.email_actions.action_in_progress = false
                })
        },

        handleSearch() {

            this.searching = true
            this.filters.last_search_text = this.search_text

            let url = `/api/admin/pre-signup-records`

            const paginate = {
                page: this.pagination.current_page,
                per_page: this.pagination.per_page
            }

            let params = { ...this.filters }

            for (const [key, value] of Object.entries(params)) {
                if (!value) {
                    delete params[key]
                }
            }

            axios.get(url, {
                    params: Object.assign(paginate, params)
                })
                .then(response => {
                    this.pagination.current_page = response.data.current_page
                    this.pagination.total = response.data.total
                    this.records = response.data.data
                })
                .catch(error => {
                    this.$root.handleErrors(error.response)
                })
                .finally(() => this.searching = false)
        },

        showCompanyInfoDialog(preSignup) {
            this.companyInfoDialog.preSignup = preSignup
            this.companyInfoDialog.visible = true
        },

    },

    watch: {
        'filters.search_text': function () {
            // reset the search results upon clearing the search input
            if (this.filters.search_text.trim() == '') {
                this.records = []
                this.resetPagination()
                this.handleSearch()
            }
        },
        'filters.pre_signup_types': function () {
            // clearing pagination
            this.resetPagination()
            // reset the search results upon clearing the search input
            if (this.filters.pre_signup_types == null) {
                this.records = []
                this.handleSearch()
            }
        },
    },

    mounted() {
        this.handleSearch()
    },

    beforeRouteEnter(to, from, next) {
        auth.check()
        .then((res) => {
            next()
        })
        .catch((err) => {
            next({name: 'Login', query: {redirect: to.fullPath}})
        })
    },

}
</script>
